import { useCallback } from 'react';
import { useMutation, type UseMutationConfig } from 'react-relay';
import type { MutationParameters, PayloadError } from 'relay-runtime';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  selectFocuses,
  selectActiveTargetIds,
  selectActiveFocusClassIds,
  setFocuses,
  setActiveTargetIds,
  setActiveFocusClassIds,
} from '../redux/reducers/focusSlice';
import {
  applyActivation,
  applyDeactivation,
  applyFocusSettingsUpdate,
  activateTarget,
  deactivateTarget,
  setTargetClassIds,
  clearTargetClassIds,
  buildActivatedFocusRows,
  buildCreateFocusInputs,
  buildUpdateFocusInputs,
} from '../businessLogicService/focusService';
import {
  CreateStudentTargetFocuses,
  UpdateStudentTargetFocuses,
  EndStudentTargetFocuses,
} from '../graphQLOperations/StudentTargetFocus';
import type { StudentTargetFocusCreateMutation } from '../graphQLOperations/__generated__/StudentTargetFocusCreateMutation.graphql';
import type { StudentTargetFocusUpdateMutation } from '../graphQLOperations/__generated__/StudentTargetFocusUpdateMutation.graphql';
import type { StudentTargetFocusEndMutation } from '../graphQLOperations/__generated__/StudentTargetFocusEndMutation.graphql';
import { ActivateFocusInput, EndFocusInput, FocusMode, MoveFocusInput, StudentTargetFocus } from '../models/UnitFocus';

function commitFocusMutation<TMutation extends MutationParameters>(
  commit: (config: UseMutationConfig<TMutation>) => unknown,
  variables: TMutation['variables'],
  errorMessage: string,
  onOk: (response: TMutation['response']) => void,
  getPayloadErrors?: (response: TMutation['response']) => ReadonlyArray<unknown> | null | undefined,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    commit({
      variables,
      onCompleted: (response: TMutation['response'], errors: PayloadError[] | null) => {
        const payloadErrors = getPayloadErrors?.(response);
        if ((errors && errors.length > 0) || (payloadErrors && payloadErrors.length > 0)) {
          reject(new Error(errorMessage));
          return;
        }
        onOk(response);
        resolve();
      },
      onError: reject,
    });
  });
}

export function useUnitFocus() {
  const dispatch = useAppDispatch();
  const focuses = useAppSelector(selectFocuses);
  const activeTargetIds = useAppSelector(selectActiveTargetIds);
  const activeFocusClassIds = useAppSelector(selectActiveFocusClassIds);
  const [commitCreateFocus] = useMutation<StudentTargetFocusCreateMutation>(CreateStudentTargetFocuses);
  const [commitUpdateFocus] = useMutation<StudentTargetFocusUpdateMutation>(UpdateStudentTargetFocuses);
  const [commitEndFocus] = useMutation<StudentTargetFocusEndMutation>(EndStudentTargetFocuses);

  const activate = useCallback(
    (input: ActivateFocusInput): Promise<void> => {
      const markActive = () => {
        dispatch(setActiveTargetIds(activateTarget(activeTargetIds, input.targetId)));
        dispatch(setActiveFocusClassIds(setTargetClassIds(activeFocusClassIds, input.targetId, input.classIds)));
      };
      const inputs = buildCreateFocusInputs(input.userIds, input.targetId, input.focusMode);
      if (inputs.length === 0) {
        markActive();
        return Promise.resolve();
      }
      return commitFocusMutation(
        commitCreateFocus,
        { input: { createStudentTargetFocusInputs: inputs, classIds: input.classIds } },
        'createStudentTargetFocuses returned errors',
        response => {
          const created = buildActivatedFocusRows(response.createStudentTargetFocuses?.studentTargetFocusNode, input);
          if (created.length > 0) dispatch(setFocuses(applyActivation(focuses, created)));
          markActive();
        },
        response => response.createStudentTargetFocuses?.errors,
      );
    },
    [commitCreateFocus, dispatch, focuses, activeTargetIds, activeFocusClassIds],
  );

  const moveFocus = useCallback(
    (move: MoveFocusInput): Promise<void> => {
      const inputs = buildCreateFocusInputs(move.userIds, move.toTargetId, move.focusMode);
      if (inputs.length === 0) return Promise.resolve();

      const activateInput: ActivateFocusInput = {
        targetId: move.toTargetId,
        userIds: move.userIds,
        classIds: move.classIds,
        focusMode: move.focusMode,
      };

      const reconcile = (created: StudentTargetFocus[]) => {
        if (created.length > 0) dispatch(setFocuses(applyActivation(focuses, created)));

        let nextActive = activateTarget(activeTargetIds, move.toTargetId);
        let nextClassIds = setTargetClassIds(activeFocusClassIds, move.toTargetId, move.classIds);

        const remainingOnOldUnit = (activeFocusClassIds[move.fromTargetId] ?? []).filter(
          id => !move.classIds.includes(id),
        );
        if (remainingOnOldUnit.length > 0) {
          nextClassIds = setTargetClassIds(nextClassIds, move.fromTargetId, remainingOnOldUnit);
        } else {
          nextActive = deactivateTarget(nextActive, move.fromTargetId);
          nextClassIds = clearTargetClassIds(nextClassIds, move.fromTargetId);
        }

        dispatch(setActiveTargetIds(nextActive));
        dispatch(setActiveFocusClassIds(nextClassIds));
      };

      return commitFocusMutation(
        commitCreateFocus,
        { input: { createStudentTargetFocusInputs: inputs, classIds: move.classIds } },
        'createStudentTargetFocuses returned errors',
        response =>
          reconcile(
            buildActivatedFocusRows(response.createStudentTargetFocuses?.studentTargetFocusNode, activateInput),
          ),
        response => response.createStudentTargetFocuses?.errors,
      );
    },
    [commitCreateFocus, dispatch, focuses, activeTargetIds, activeFocusClassIds],
  );

  const updateFocusSettings = useCallback(
    (targetId: string, focusMode: FocusMode): Promise<void> => {
      const inputs = buildUpdateFocusInputs(focuses, targetId, focusMode);
      if (inputs.length === 0) return Promise.resolve();
      return commitFocusMutation(
        commitUpdateFocus,
        { input: { updateStudentTargetFocusInputs: inputs } },
        'updateStudentTargetFocuses returned errors',
        () => dispatch(setFocuses(applyFocusSettingsUpdate(focuses, targetId, focusMode))),
        response => response.updateStudentTargetFocuses?.errors,
      );
    },
    [commitUpdateFocus, dispatch, focuses],
  );

  const endFocus = useCallback(
    (input: EndFocusInput): Promise<void> => {
      const reconcile = () => {
        const remaining = (activeFocusClassIds[input.targetId] ?? []).filter(id => !input.classIds.includes(id));
        if (input.endsWholeTarget || remaining.length === 0) {
          dispatch(setFocuses(applyDeactivation(focuses, input.targetId)));
          dispatch(setActiveTargetIds(deactivateTarget(activeTargetIds, input.targetId)));
          dispatch(setActiveFocusClassIds(clearTargetClassIds(activeFocusClassIds, input.targetId)));
        } else {
          dispatch(setFocuses(applyDeactivation(focuses, input.targetId, input.userIds)));
          dispatch(setActiveFocusClassIds(setTargetClassIds(activeFocusClassIds, input.targetId, remaining)));
        }
      };
      if (input.userIds.length === 0) {
        reconcile();
        return Promise.resolve();
      }
      return commitFocusMutation(
        commitEndFocus,
        { input: { userIds: input.userIds, classIds: input.classIds } },
        'endStudentTargetFocuses returned errors',
        () => reconcile(),
      );
    },
    [commitEndFocus, dispatch, focuses, activeTargetIds, activeFocusClassIds],
  );

  return {
    focuses,
    activeTargetIds,
    activeFocusClassIds,
    activate,
    moveFocus,
    updateFocusSettings,
    endFocus,
  };
}
